import React from 'react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  mergeUniqueEmails,
  normalizeCcRecipients,
  splitEmails,
} from './ccRecipients';

interface CcRecipientsFieldProps {
  value: string[];
  onChange: (value: string[]) => void;
  errorMessage?: string;
}

interface CommaSeparatedInputResult {
  recipients: string[];
  remainingInput: string;
}

type InputChangeReason = 'input' | 'reset' | 'clear';

function parseCommaSeparatedInput(
  nextValue: string,
): CommaSeparatedInputResult {
  const hasTrailingComma = nextValue.endsWith(',');
  return {
    recipients: splitEmails(nextValue),
    remainingInput: hasTrailingComma
      ? ''
      : nextValue.slice(nextValue.lastIndexOf(',') + 1).trim(),
  };
}

function getHelperText(errorMessage?: string): string {
  if (errorMessage) return errorMessage;
  return 'multiple emails allowed separated by comma';
}

function CcRecipientsField({
  value,
  onChange,
  errorMessage,
}: CcRecipientsFieldProps) {
  const [inputValue, setInputValue] = React.useState('');

  function addRecipientsFromInput(input: string): void {
    const parsedRecipients = splitEmails(input);
    if (parsedRecipients.length === 0) return;

    onChange(mergeUniqueEmails(value, parsedRecipients));
    setInputValue('');
  }

  function handleRecipientsChange(nextValue: string[]): void {
    onChange(normalizeCcRecipients(nextValue));
  }

  function handleInputChange(
    nextValue: string,
    reason: InputChangeReason,
  ): void {
    if (reason !== 'input') {
      setInputValue(nextValue);
      return;
    }

    if (!nextValue.includes(',')) {
      setInputValue(nextValue);
      return;
    }

    const { recipients, remainingInput } = parseCommaSeparatedInput(nextValue);
    if (recipients.length > 0) onChange(mergeUniqueEmails(value, recipients));
    setInputValue(remainingInput);
  }

  function handleBlur(): void {
    addRecipientsFromInput(inputValue);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
    if (event.key !== 'Enter') return;
    if (!inputValue.trim()) return;

    event.preventDefault();
    addRecipientsFromInput(inputValue);
  }

  return (
    <Autocomplete<string, true, false, true>
      multiple
      freeSolo
      options={[]}
      value={value}
      inputValue={inputValue}
      onChange={(_, nextValue) => handleRecipientsChange(nextValue)}
      onInputChange={(_, nextValue, reason) =>
        handleInputChange(nextValue, reason)
      }
      filterSelectedOptions
      renderInput={params => (
        <TextField
          {...params}
          label="Additional recipients"
          variant="outlined"
          fullWidth
          error={!!errorMessage}
          helperText={getHelperText(errorMessage)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      )}
    />
  );
}

export default CcRecipientsField;
